/**
 * PO-2026-06 — Admin Message Center, split into two clearly-separated tabs:
 *   • Direct Messages   — 1:1 conversations with individual talent (searchable
 *                          by name/email). Clicking one opens the canonical DM.
 *   • Event / Group     — per-gig group channels (existing EventChannelDetail).
 *
 * No more hunting through event channels to find a private conversation.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import {
  ArrowLeft, Loader2, MessageSquare, Search, Users, Calendar, ChevronRight, Mail, Sparkles, Send, Building2, PenSquare, Plus,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDateMonthDay } from '../../utils/timeFormat';

const AdminMessages = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initTab = params.get('tab');
  const [tab, setTab] = useState(initTab === 'events' ? 'events' : initTab === 'clients' ? 'clients' : 'direct');

  return (
    <div className="min-h-screen bg-slate-50 pb-16" data-testid="admin-messages-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/home')} data-testid="back-to-home">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <h1 className="text-xl md:text-2xl font-extrabold flex items-center gap-2 text-slate-900">
            <MessageSquare className="w-5 h-5 text-cyan-500" /> Messages
          </h1>
          <div className="ml-auto">
            <Button variant="outline" size="sm" onClick={() => navigate('/admin/ai-support')} data-testid="ai-escalations-link"
              className="border-indigo-300 text-indigo-700 hover:bg-indigo-50">
              <Sparkles className="w-4 h-4 mr-1" /> AI Escalations
            </Button>
          </div>
        </div>
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex gap-1 border-b border-slate-200">
            <TabButton active={tab === 'direct'} onClick={() => setTab('direct')} testid="tab-direct">
              Talent DMs
            </TabButton>
            <TabButton active={tab === 'clients'} onClick={() => setTab('clients')} testid="tab-clients">
              Client Messages
            </TabButton>
            <TabButton active={tab === 'events'} onClick={() => setTab('events')} testid="tab-events">
              Event / Group
            </TabButton>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {tab === 'direct' && <DirectMessages />}
        {tab === 'clients' && <ClientMessages initialThread={params.get('thread') || ''} />}
        {tab === 'events' && <EventChannels initialSearch={params.get('event') || ''} />}
      </div>
    </div>
  );
};

const TabButton = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
      active ? 'border-cyan-500 text-cyan-700' : 'border-transparent text-slate-500 hover:text-slate-700'
    }`}
  >
    {children}
  </button>
);

const DirectMessages = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [threads, setThreads] = useState([]);
  const [q, setQ] = useState('');

  const load = useCallback(async (search) => {
    setLoading(true);
    try {
      const r = await api.get('/admin/messages/dm/threads', { params: search ? { search } : {} });
      setThreads(r.data?.threads || []);
    } catch (e) {
      toast.error('Failed to load direct messages');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(''); }, [load]);

  const initials = (name) => (name || 'T').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className="space-y-3">
      <form onSubmit={(e) => { e.preventDefault(); load(q); }} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search talent by name or email…" className="pl-9" data-testid="dm-search-input" />
        </div>
        <Button type="submit" data-testid="dm-search-btn">Search</Button>
      </form>

      {loading ? (
        <div className="grid place-items-center py-16"><Loader2 className="w-6 h-6 animate-spin text-cyan-600" /></div>
      ) : threads.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-500" data-testid="dm-empty">
          No direct conversations yet. Open one from any talent's <span className="font-semibold">Message</span> button.
        </div>
      ) : (
        <div className="space-y-2" data-testid="dm-threads-container">
          {threads.map((t) => (
            <button
              key={t.thread_id}
              onClick={() => navigate(`/admin/messages/dm/${t.talent_id}`)}
              className="w-full bg-white border border-slate-200 hover:border-cyan-300 hover:shadow-sm transition-all rounded-2xl px-4 py-3 text-left flex items-center gap-3"
              data-testid={`dm-thread-${t.talent_id}`}
            >
              <Avatar className="w-10 h-10 shrink-0">
                {t.talent_photo ? <AvatarImage src={t.talent_photo} alt={t.talent_name} /> : null}
                <AvatarFallback className="bg-cyan-100 text-cyan-700 text-xs">{initials(t.talent_name)}</AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-slate-900 truncate">{t.talent_name}</span>
                  {t.unread_count > 0 && (
                    <Badge className="bg-cyan-600 text-white border-0 text-[10px]" data-testid={`dm-unread-${t.talent_id}`}>{t.unread_count} new</Badge>
                  )}
                </div>
                <div className="text-xs text-slate-500 truncate mt-0.5">
                  {t.last_message ? (
                    <>{t.last_from_talent ? '' : 'You: '}{t.last_message}</>
                  ) : (
                    <span className="italic text-slate-400">No messages yet</span>
                  )}
                </div>
                {t.origin_label && (
                  <div className="text-[10px] text-amber-700 mt-0.5 truncate">{t.origin_label}</div>
                )}
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400 shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const EventChannels = ({ initialSearch }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [channels, setChannels] = useState([]);
  const [q, setQ] = useState(initialSearch || '');

  const load = useCallback(async (searchTerm) => {
    setLoading(true);
    try {
      const r = await api.get('/admin/messages/channels', {
        params: searchTerm ? { search: searchTerm, limit: 200 } : { limit: 200 },
      });
      setChannels(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error('Failed to load event channels');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(initialSearch); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-3">
      <form onSubmit={(e) => { e.preventDefault(); load(q); }} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by event name or venue…" className="pl-9" data-testid="admin-messages-search" />
        </div>
        <Button type="submit" data-testid="admin-messages-search-btn">Search</Button>
      </form>

      {loading ? (
        <div className="grid place-items-center py-16"><Loader2 className="w-6 h-6 animate-spin text-cyan-600" /></div>
      ) : channels.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-500">No event channels yet.</div>
      ) : (
        channels.map((c) => (
          <button
            key={c.id}
            onClick={() => navigate(`/messages/channel/${c.id}`)}
            className="w-full bg-white border border-slate-200 hover:border-cyan-300 hover:shadow-sm transition-all rounded-2xl px-4 py-3 text-left flex items-center justify-between gap-3"
            data-testid={`admin-channel-row-${c.id}`}
          >
            <div className="min-w-0 flex-1">
              <div className="font-extrabold text-slate-900 truncate">{c.title || c.venue_name || 'Untitled channel'}</div>
              <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-3 flex-wrap">
                {c.venue_name && c.title !== c.venue_name && (
                  <span className="inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {c.venue_name}</span>
                )}
                {c.shift_date && (
                  <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDateMonthDay(c.shift_date)}</span>
                )}
                <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {c.member_count} member{c.member_count !== 1 ? 's' : ''}</span>
                <span>· {c.message_count} message{c.message_count !== 1 ? 's' : ''}</span>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
          </button>
        ))
      )}
    </div>
  );
};

const ClientMessages = ({ initialThread }) => {
  const [loading, setLoading] = useState(true);
  const [threads, setThreads] = useState([]);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(null); // {thread_id, client}
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  // New-conversation client picker (searches the real client database).
  const [pickerOpen, setPickerOpen] = useState(false);
  const [clientQ, setClientQ] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [clientLoading, setClientLoading] = useState(false);
  const [startables, setStartables] = useState([]); // client-DB matches shown under threads
  const searchTimer = useRef(null);
  // Bulk Announce
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkQ, setBulkQ] = useState('');
  const [bulkResults, setBulkResults] = useState([]);
  const [bulkSelected, setBulkSelected] = useState({}); // user_id -> client
  const [bulkMsg, setBulkMsg] = useState('');
  const [bulkSending, setBulkSending] = useState(false);
  const bulkTimer = useRef(null);

  const searchClients = useCallback(async (term) => {
    setClientLoading(true);
    try {
      // Manually encode so '+' in emails isn't decoded as a space server-side.
      const r = await api.get(`/admin/messages/client-dm/search-clients?q=${encodeURIComponent(term || '')}`);
      setClientResults(r.data?.clients || []);
      return r.data?.clients || [];
    } catch {
      toast.error('Failed to search clients');
      return [];
    } finally {
      setClientLoading(false);
    }
  }, []);

  const loadThreads = useCallback(async (search) => {
    try {
      const r = await api.get('/admin/messages/client-dm/threads', { params: search ? { search } : {} });
      setThreads(r.data?.threads || []);
      return r.data?.threads || [];
    } catch {
      toast.error('Failed to load client messages');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const openThread = useCallback(async (thread_id) => {
    try {
      const r = await api.get(`/admin/messages/client-dm/thread/${thread_id}`);
      setActive({ thread_id, client: r.data.client });
      setMessages(r.data.messages || []);
      try { window.dispatchEvent(new Event('admin-client-dm-updated')); } catch (_e) { /* noop */ }
    } catch {
      toast.error('Failed to open conversation');
    }
  }, []);

  useEffect(() => {
    (async () => {
      const list = await loadThreads('');
      if (initialThread && list.some((t) => t.thread_id === initialThread)) {
        openThread(initialThread);
      }
    })();
  }, [loadThreads, openThread, initialThread]);

  // Auto-refresh the open conversation every 8s (near real-time).
  useEffect(() => {
    if (!active?.thread_id) return undefined;
    const id = setInterval(() => {
      api.get(`/admin/messages/client-dm/thread/${active.thread_id}`)
        .then((r) => setMessages(r.data.messages || []))
        .catch(() => {});
    }, 8000);
    return () => clearInterval(id);
  }, [active]);

  const send = async () => {
    const content = draft.trim();
    if (!content || !active) return;
    setSending(true);
    try {
      await api.post(`/admin/messages/client-dm/thread/${active.thread_id}/send`, { content });
      setDraft('');
      const r = await api.get(`/admin/messages/client-dm/thread/${active.thread_id}`);
      setMessages(r.data.messages || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const initials = (name) => (name || 'C').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();

  // Open (or create — never duplicate) the company conversation for a picked contact.
  const selectClient = useCallback(async (client) => {
    try {
      const r = await api.post('/admin/messages/client-dm/open', { client_id: client.user_id });
      const t = await api.get(`/admin/messages/client-dm/thread/${r.data.thread_id}`);
      setActive({
        thread_id: r.data.thread_id,
        client: {
          ...(t.data.client || {}),
          name: client.company_name || t.data.client?.name,
          contact_name: client.contact_name,
          email: client.email || t.data.client?.email,
        },
      });
      setMessages(t.data.messages || []);
      setPickerOpen(false);
      try { window.dispatchEvent(new Event('admin-client-dm-updated')); } catch (_e) { /* noop */ }
    } catch {
      toast.error('Failed to open conversation');
    }
  }, []);

  const openPicker = useCallback(() => {
    setPickerOpen(true);
    setClientQ('');
    searchClients('');
  }, [searchClients]);

  // Debounced typeahead for the picker — filters live as you type.
  const onPickerType = useCallback((val) => {
    setClientQ(val);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => searchClients(val), 300);
  }, [searchClients]);

  const searchBulk = useCallback(async (term) => {
    try {
      const r = await api.get(`/admin/messages/client-dm/search-clients?q=${encodeURIComponent(term || '')}`);
      setBulkResults(r.data?.clients || []);
    } catch { /* silent */ }
  }, []);

  const onBulkType = useCallback((val) => {
    setBulkQ(val);
    if (bulkTimer.current) clearTimeout(bulkTimer.current);
    bulkTimer.current = setTimeout(() => searchBulk(val), 300);
  }, [searchBulk]);

  const toggleBulk = (client) => {
    setBulkSelected((prev) => {
      const next = { ...prev };
      if (next[client.user_id]) delete next[client.user_id];
      else next[client.user_id] = client;
      return next;
    });
  };

  const sendBulk = async () => {
    const ids = Object.keys(bulkSelected);
    const content = bulkMsg.trim();
    if (!content || ids.length === 0) return;
    setBulkSending(true);
    try {
      const r = await api.post('/admin/messages/client-dm/broadcast', { client_ids: ids, content });
      toast.success(`Announcement sent to ${r.data.sent} client${r.data.sent !== 1 ? 's' : ''}.`);
      setBulkOpen(false);
      setBulkSelected({});
      setBulkMsg('');
      setBulkQ('');
      loadThreads('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send announcement');
    } finally {
      setBulkSending(false);
    }
  };

  const openBulk = () => {
    setBulkOpen(true);
    setBulkSelected({});
    setBulkMsg('');
    setBulkQ('');
    searchBulk('');
  };

  // Combined search: existing threads + startable clients from the DB.
  const runSearch = useCallback(async (term) => {
    setLoading(true);
    const [thr, cli] = await Promise.all([loadThreads(term), searchClients(term)]);
    const threadOwnerIds = new Set((thr || []).map((t) => String(t.client_id)));
    setStartables((cli || []).filter((c) => !threadOwnerIds.has(String(c.owner_id))));
  }, [loadThreads, searchClients]);

  if (active) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl flex flex-col h-[70vh]" data-testid="admin-client-conversation">
        <div className="flex items-center gap-3 border-b border-slate-200 px-4 py-3">
          <Button variant="ghost" size="sm" onClick={() => { setActive(null); loadThreads(q); }} data-testid="client-conv-back">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <Avatar className="w-9 h-9">
            {active.client?.photo ? <AvatarImage src={active.client.photo} /> : null}
            <AvatarFallback className="bg-cyan-100 text-cyan-700 text-xs">{initials(active.client?.name)}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="font-bold text-slate-900 truncate" data-testid="client-conv-company">{active.client?.name}</div>
            <div className="text-xs text-slate-400 truncate">
              {active.client?.contact_name ? <span className="font-medium text-slate-500">{active.client.contact_name}</span> : null}
              {active.client?.contact_name && active.client?.email ? ' · ' : ''}
              {active.client?.email}
            </div>
          </div>
          <span className="ml-auto text-[10px] text-slate-400 hidden sm:block">Shared with this client's team</span>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2" data-testid="client-conv-messages">
          {messages.length === 0 ? (
            <div className="text-center text-sm text-slate-400 py-10">No messages yet. Say hello.</div>
          ) : messages.map((m) => (
            <div key={m.id} className={`flex ${m.from_admin ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${m.from_admin ? 'bg-cyan-600 text-white' : 'bg-slate-100 text-slate-800'}`}>
                {!m.from_admin && <div className="text-[10px] font-semibold opacity-70 mb-0.5">{m.sender_name}</div>}
                {m.content}
              </div>
            </div>
          ))}
        </div>
        <div className="border-t border-slate-200 p-3 flex gap-2">
          <Input value={draft} onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); send(); } }}
            placeholder="Reply to the client…" data-testid="client-conv-input" />
          <Button onClick={send} disabled={sending || !draft.trim()} data-testid="client-conv-send">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-slate-500 hidden sm:block">Message any client — search your client database to start a new conversation.</p>
        <div className="flex items-center gap-2 ml-auto">
          <Button variant="outline" onClick={openBulk} className="border-cyan-300 text-cyan-700 hover:bg-cyan-50" data-testid="client-dm-bulk-btn">
            <Users className="w-4 h-4 mr-1.5" /> Bulk Announce
          </Button>
          <Button onClick={openPicker} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="client-dm-new-message-btn">
            <PenSquare className="w-4 h-4 mr-1.5" /> New Message
          </Button>
        </div>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); runSearch(q); }} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clients by company, contact or email…" className="pl-9" data-testid="client-dm-search-input" />
        </div>
        <Button type="submit" data-testid="client-dm-search-btn">Search</Button>
      </form>

      {loading ? (
        <div className="grid place-items-center py-16"><Loader2 className="w-6 h-6 animate-spin text-cyan-600" /></div>
      ) : (
        <div className="space-y-4">
          {threads.length > 0 && (
            <div className="space-y-2" data-testid="client-dm-threads-container">
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Conversations</div>
              {threads.map((t) => (
                <button key={t.thread_id} onClick={() => openThread(t.thread_id)}
                  className="w-full bg-white border border-slate-200 hover:border-cyan-300 hover:shadow-sm transition-all rounded-2xl px-4 py-3 text-left flex items-center gap-3"
                  data-testid={`client-dm-thread-${t.client_id}`}>
                  <Avatar className="w-10 h-10 shrink-0">
                    {t.client_photo ? <AvatarImage src={t.client_photo} alt={t.client_name} /> : null}
                    <AvatarFallback className="bg-cyan-100 text-cyan-700 text-xs"><Building2 className="w-4 h-4" /></AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-slate-900 truncate">{t.client_name}</span>
                      {t.unread_count > 0 && (
                        <Badge className="bg-cyan-600 text-white border-0 text-[10px]" data-testid={`client-dm-unread-${t.client_id}`}>{t.unread_count} new</Badge>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 truncate mt-0.5">
                      {t.contact_name ? <span className="font-medium text-slate-600">{t.contact_name} · </span> : null}
                      {t.last_message || <span className="italic text-slate-400">No messages yet</span>}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400 shrink-0" />
                </button>
              ))}
            </div>
          )}

          {startables.length > 0 && (
            <div className="space-y-2" data-testid="client-dm-startables-container">
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Start a new conversation</div>
              {startables.map((c) => (
                <button key={c.user_id} onClick={() => selectClient(c)}
                  className="w-full bg-white border border-dashed border-slate-300 hover:border-cyan-400 hover:shadow-sm transition-all rounded-2xl px-4 py-3 text-left flex items-center gap-3"
                  data-testid={`client-dm-startable-${c.user_id}`}>
                  <Avatar className="w-10 h-10 shrink-0">
                    {c.logo_url ? <AvatarImage src={c.logo_url} alt={c.company_name} /> : null}
                    <AvatarFallback className="bg-slate-100 text-slate-500 text-xs"><Building2 className="w-4 h-4" /></AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-slate-900 truncate">{c.company_name}</div>
                    <div className="text-xs text-slate-500 truncate mt-0.5">{c.contact_name} · {c.email}</div>
                  </div>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-cyan-700 shrink-0"><Plus className="w-4 h-4" /> Message</span>
                </button>
              ))}
            </div>
          )}

          {threads.length === 0 && startables.length === 0 && (
            <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-500" data-testid="client-dm-empty">
              {q ? `No clients or conversations match "${q}". Try the New Message button to browse all clients.` : 'No client conversations yet — tap New Message to start one.'}
            </div>
          )}
        </div>
      )}

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-lg" data-testid="client-picker-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><PenSquare className="w-5 h-5 text-cyan-600" /> New Client Message</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                autoFocus
                value={clientQ}
                onChange={(e) => onPickerType(e.target.value)}
                placeholder="Search by company, contact name or email…"
                className="pl-9"
                data-testid="client-picker-search"
              />
            </div>
            <div className="max-h-[50vh] overflow-y-auto space-y-1.5" data-testid="client-picker-results">
              {clientLoading ? (
                <div className="grid place-items-center py-10"><Loader2 className="w-5 h-5 animate-spin text-cyan-600" /></div>
              ) : clientResults.length === 0 ? (
                <div className="text-center text-sm text-slate-400 py-8">No matching clients.</div>
              ) : clientResults.map((c) => (
                <button key={c.user_id} onClick={() => selectClient(c)}
                  className="w-full border border-slate-200 hover:border-cyan-300 hover:bg-cyan-50/40 transition-all rounded-xl px-3 py-2.5 text-left flex items-center gap-3"
                  data-testid={`client-picker-row-${c.user_id}`}>
                  <Avatar className="w-9 h-9 shrink-0">
                    {c.logo_url ? <AvatarImage src={c.logo_url} alt={c.company_name} /> : null}
                    <AvatarFallback className="bg-slate-100 text-slate-500 text-xs"><Building2 className="w-4 h-4" /></AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 truncate flex items-center gap-2">
                      {c.company_name}
                      {c.has_thread && <Badge variant="outline" className="text-[10px] text-cyan-700 border-cyan-200">existing</Badge>}
                    </div>
                    <div className="text-xs text-slate-500 truncate">{c.contact_name} · {c.email}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                </button>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-lg" data-testid="bulk-announce-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Users className="w-5 h-5 text-cyan-600" /> Bulk Announce</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Send one message to several clients at once (event updates, outages). Each client gets it in their own private conversation.</p>
            <Textarea value={bulkMsg} onChange={(e) => setBulkMsg(e.target.value)} rows={3}
              placeholder="Type your announcement…" data-testid="bulk-announce-message" />
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={bulkQ} onChange={(e) => onBulkType(e.target.value)}
                placeholder="Search clients to add…" className="pl-9" data-testid="bulk-announce-search" />
            </div>
            {Object.keys(bulkSelected).length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="bulk-selected-chips">
                {Object.values(bulkSelected).map((c) => (
                  <span key={c.user_id} className="inline-flex items-center gap-1 bg-cyan-100 text-cyan-800 text-xs rounded-full px-2 py-0.5">
                    {c.company_name}
                    <button onClick={() => toggleBulk(c)} className="hover:text-cyan-950" data-testid={`bulk-remove-${c.user_id}`}>×</button>
                  </span>
                ))}
              </div>
            )}
            <div className="max-h-[38vh] overflow-y-auto space-y-1" data-testid="bulk-announce-results">
              {bulkResults.length === 0 ? (
                <div className="text-center text-sm text-slate-400 py-6">No matching clients.</div>
              ) : bulkResults.map((c) => (
                <label key={c.user_id} className="flex items-center gap-3 border border-slate-200 rounded-xl px-3 py-2 cursor-pointer hover:bg-slate-50" data-testid={`bulk-row-${c.user_id}`}>
                  <Checkbox checked={!!bulkSelected[c.user_id]} onCheckedChange={() => toggleBulk(c)} data-testid={`bulk-check-${c.user_id}`} />
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 truncate text-sm">{c.company_name}</div>
                    <div className="text-xs text-slate-500 truncate">{c.contact_name} · {c.email}</div>
                  </div>
                </label>
              ))}
            </div>
            <Button onClick={sendBulk} disabled={bulkSending || !bulkMsg.trim() || Object.keys(bulkSelected).length === 0}
              className="w-full bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="bulk-announce-send">
              {bulkSending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send to {Object.keys(bulkSelected).length} client{Object.keys(bulkSelected).length !== 1 ? 's' : ''}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMessages;